/** Typed client for the PRD section 10 contract. */

export type ClueOffer = {
  type: string;
  label: string;
  /** The clue asked out loud: "Are you anywhere near the sea?" */
  question: string;
  icon: string;
  cost: number | null;
  bought: boolean;
  answer: string | null;
  locked: boolean;
  lockedReason: string | null;
};

/** The board the hider declared, handed to the seeker before they spend. */
export type Arena = { token: string; label: string };

export type ArenaDetail = Arena & {
  count: number;
  bounds: {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
  } | null;
};

export type ArenaOption = {
  token: string;
  label: string;
  kind: "world" | "continent" | "country" | "region";
  count: number;
  /** Present on countries only: the cue for the "Regions →" affordance. */
  countryCode?: string;
};

export type Trend = "warmer" | "colder" | "same" | null;

/** A guess as the seeker sees it mid-round: a temperature, not a radius. */
export type GuessView = {
  lat: number;
  lng: number;
  band: number;
  label: string;
  trend: Trend;
};

export type Result = {
  status: "found" | "failed" | "resigned";
  place: {
    name: string;
    admin1: string | null;
    countryName: string;
    countryCode: string;
    lat: number;
    lng: number;
    blurb: string;
  };
  seekerScore: number;
  hiderScore: number;
  spend: number;
  guessesUsed: number;
  bestDistanceKm: number;
  winRadiusKm: number;
  arena: Arena;
  durationSeconds: number;
  cluesBought: {
    type: string;
    label: string;
    icon: string;
    answer: string;
    cost: number;
  }[];
  note: string | null;
  hiderName: string | null;
  shareText: string;
};

export type Place = {
  id: number;
  name: string;
  admin1: string | null;
  countryName: string;
  countryCode: string;
  lat: number;
  lng: number;
};

export type AttemptStart = {
  attemptId: string;
  budgetRemaining: number;
  guessesRemaining: number;
  guessCost: number;
  candidateCount: number;
  clues: ClueOffer[];
  arena: Arena;
  winRadiusKm: number;
};

export type AttemptState = AttemptStart & {
  status: "active" | "found" | "failed" | "resigned";
  guesses: GuessView[];
};

/** Where the answer can still be — cells, or a bounding box near the end. */
export type Region = {
  count: number;
  mode: "cells" | "bounds";
  size?: number;
  cells: { lat: number; lng: number; d: number }[];
  bounds?: {
    min_lat: number;
    max_lat: number;
    min_lng: number;
    max_lng: number;
  } | null;
};

export type CluePurchase = {
  type: string;
  label: string;
  answer: string;
  cost: number;
  budgetRemaining: number;
  /** Asking costs looking: the counter has to move when a question is bought. */
  guessesRemaining: number;
  candidateCount: number;
  clues: ClueOffer[];
};

export type GuessOutcome = {
  correct: boolean;
  band: number;
  label: string;
  trend: Trend;
  cost: number;
  guessesRemaining: number;
  candidateCount: number;
  budgetRemaining: number;
  clues: ClueOffer[];
  guesses: GuessView[];
  result?: Result;
};

export type PinSummary = {
  exists: boolean;
  hiderName: string | null;
  attemptCount: number;
  foundCount: number;
  avgSpend: number | null;
  isDaily: boolean;
  arena: ArenaDetail;
  winRadiusKm: number;
};

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(
      (body as { error?: string }).error ?? `http_${res.status}`,
      res.status,
    );
  }
  return body as T;
}

export const api = {
  createPin: (input: {
    placeId: number;
    arena: string;
    hiderName?: string;
    notes: string[];
    note?: string;
  }) =>
    call<{
      pinId: string;
      shareUrl: string;
      hiderToken: string;
      arena: ArenaDetail;
      winRadiusKm: number;
    }>("/api/pins", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** Boards a hider can pick, with the candidate count that defines each. */
  arenas: (q?: string) =>
    call<{ arenas: ArenaOption[] }>(
      `/api/arenas${q ? `?q=${encodeURIComponent(q)}` : ""}`,
    ),

  regions: (countryCode: string) =>
    call<{ arenas: ArenaOption[] }>(`/api/arenas?country=${countryCode}`),

  arena: (token: string) =>
    call<{ arena: ArenaDetail; winRadiusKm: number; playable: boolean }>(
      `/api/arenas?token=${encodeURIComponent(token)}`,
    ),

  getPin: (pinId: string) => call<PinSummary>(`/api/pins/${pinId}`),

  getAttempt: (attemptId: string) =>
    call<AttemptState>(`/api/attempts/${attemptId}`),

  region: (attemptId: string) =>
    call<Region>(`/api/attempts/${attemptId}/region`),

  startAttempt: (pinId: string, seekerName?: string) =>
    call<AttemptStart>("/api/attempts", {
      method: "POST",
      body: JSON.stringify({ pinId, seekerName }),
    }),

  buyClue: (attemptId: string, type: string) =>
    call<CluePurchase>(`/api/attempts/${attemptId}/clues`, {
      method: "POST",
      body: JSON.stringify({ type }),
    }),

  guess: (attemptId: string, lat: number, lng: number, placeId?: number) =>
    call<GuessOutcome>(`/api/attempts/${attemptId}/guesses`, {
      method: "POST",
      body: JSON.stringify({ lat, lng, placeId }),
    }),

  resign: (attemptId: string) =>
    call<{ result: Result }>(`/api/attempts/${attemptId}/resign`, {
      method: "POST",
    }),

  result: (attemptId: string) => call<Result>(`/api/attempts/${attemptId}/result`),

  daily: () =>
    call<{ pinId: string; date: string; playedToday: boolean }>("/api/daily"),

  /** Snap a dropped pin to the nearest hideable place on the board (PRD 3.1). */
  nearest: (lat: number, lng: number, arena?: string) =>
    call<{ place: Place & { distanceKm: number } }>(
      `/api/places/nearest?lat=${lat}&lng=${lng}${
        arena ? `&arena=${encodeURIComponent(arena)}` : ""
      }`,
    ),

  search: (q: string, limit = 10, arena?: string) =>
    call<{ places: Place[] }>(
      `/api/places/search?q=${encodeURIComponent(q)}&limit=${limit}${
        arena ? `&arena=${encodeURIComponent(arena)}` : ""
      }`,
    ),

  stats: (pinId: string, token: string) =>
    call<PinStats>(`/api/pins/${pinId}/stats?token=${encodeURIComponent(token)}`),
};

export type PinStats = {
  pinId: string;
  hiderName: string | null;
  arenaLabel: string;
  winRadiusKm: number;
  place: { name: string; countryName: string; lat: number; lng: number };
  attemptCount: number;
  foundCount: number;
  avgSpend: number | null;
  avgHiderScore: number | null;
  guesses: { lat: number; lng: number; distanceKm: number; correct: boolean }[];
  clueCounts: { type: string; label: string; icon: string; count: number }[];
};

/**
 * Local record of which attempt belongs to which pin, so a returning player
 * resumes instead of starting a fresh round. There are no accounts (PRD 2.5),
 * so this is the only continuity we have.
 */
export const attemptStore = {
  key: (pinId: string) => `hidein.attempt.${pinId}`,
  get(pinId: string): string | null {
    try {
      return localStorage.getItem(this.key(pinId));
    } catch {
      return null;
    }
  },
  set(pinId: string, attemptId: string) {
    try {
      localStorage.setItem(this.key(pinId), attemptId);
    } catch {
      /* private mode: fall back to a fresh attempt each visit */
    }
  },
  clear(pinId: string) {
    try {
      localStorage.removeItem(this.key(pinId));
    } catch {
      /* ignore */
    }
  },
};
